import {getRandomColor} from "../../../utils/GetRandomColor";
import { MathJax, MathJaxContext } from "better-react-mathjax";

export default function Statistics() {
    const imgStyle = {
        backgroundColor: "#E9E8E6",
        border: `5px solid ${getRandomColor()}`,
    }

    const altTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
        textAlign: "center",
        marginTop: "-10px",
        fontStyle: "italic",
    }

    const mathJaxConfig = {
        loader: {load: ["[tex]/html"]},
        tex: {
            packages: {"[+]": ["html"]},
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]]
        }
    }

    return (
        <MathJaxContext config={mathJaxConfig}>
        <div className={"blog-body"}>
            <h2>Statistics</h2>
            <p>
                Statistics is based on probability theory and is used for sampling and inference in the most
                general sense. Various statistical measures help to identify the structure of the sample,
                while inferential statistics aim to infer properties from the data when descriptive statistics
                are not sufficient. This section includes these two topics as main headings.
                Statistics is the cornerstone of data science, it is impossible to distinguish them. Even
                more, statistics occupies such an important place in data science that scholars once argued
                about whether or not statistics should be called data science (Diggle, 2015).
            </p>

            <h3>Measures</h3>
            <p>
                In statistics, different types of measurement methods are used to determine the behaviour of
                data.
                These methods are used to find out how the sample behaves around the centre, how skewed it is,
                how
                much variability it has, or how different variables affect each other. Four types of statistical
                measurement methods play an important role, especially in machine learning.
            </p>

            <h4>Measures of Central Tendency</h4>
            <p>
                The measure of central tendency, one of the most fundamental concepts in statistics, gives the
                average of the sample in different approaches.
            </p>

            <h5>Mean</h5>
            <p>
                The mean is the average of the data. The population mean is denoted with <MathJax inline>{'$\\mu$'}</MathJax> and calculated with

                <MathJax>{'\\begin{align}\n\\mu=\\frac{\\sum_{i=1}^nx_i}{n}\n\\end{align}'}</MathJax>

                while the sample mean is denoted by <MathJax inline>{'$\\bar{x}$'}</MathJax>, and calculated with

                <MathJax>{'\\begin{align}\n\\bar{x}=\\frac{\\sum_{i=1}^Nx_i}{N}\n\\end{align}'}</MathJax>

                The mean is easily affected by the outliers.
            </p>

            <h5>Median</h5>
            <p>
                The median is the number in the middle of the sorted data.
            </p>

            <h5>Mode</h5>
            <p>
                The mod is the number that most occurs in the data set.

                Mean, median, and mode needed to be used together to get enough conclusions about the data.
            </p>

            <h4>Measures of Asymmetry</h4>
            <p>
                Asymmetry measures are used to examine whether a data set has an unequal distribution on
                opposite
                sides of the mean.
            </p>

            <h5>Skewness</h5>
            <p>
                Indicates if the data is distributed unevenly on a side or not. Skewness is calculated with

                <MathJax>{'\\begin{align}\n\\frac{\\frac{1}{n} \\sum_{i=1}^{n}\\left(x_{i}-\\bar{x}\\right)^{3}}{\\sqrt{\\frac{1}{n-1}\n\\sum_{i=1}^{n}\\left(x_{i}-\\bar{x}\\right)^{2}}^{3}}\n\\end{align}'}</MathJax>

                Three different types skewness can be observed

                <ul>
                    <li>When the mean is greater than the median, the distribution has positive (right) skewness.
                    </li>
                    <li>If the mean is equal to the median, the distribution has no skewness, i.e., it is symmetric.
                    </li>
                    <li>If the mean is smaller than the median, the distribution has negative (left) skew.</li>
                </ul>
            </p>

            <h4>Measures of Variability</h4>
            <p>
                Measures of variability illustrate the irregularities in a data set. The lower the values of the
                variability measure, the more similar the data values are in a set.
            </p>

            <h5>Variance</h5>
            <p>
                Variance indicates how much the data deviates from the mean. The variance of the population is
                denoted by <MathJax inline>{'$\\sigma^2$'}</MathJax>. It is one of the basic measures along with the expected value. The
                population
                variance is calculated with

                <MathJax>{'\\begin{align}\n\\sigma^{2}=\\frac{\\sum_{i=1}^{N}\\left(x_{i}-\\mu\\right)^{2}}{N}\n\\end{align}'}</MathJax>

                And the sample variance is denoted with <MathJax inline>{'$s^2$'}</MathJax>, it is calculated with

                <MathJax>{'\\begin{align}\ns^{2}=\\frac{\\sum_{i=1}^{n}\\left(x_{i}-\\bar{x}\\right)^{2}}{n-1}\n\\end{align}'}</MathJax>
            </p>

            <h5>Standard Deviation</h5>
            <p>
                The standard deviation is the square root of the variance and is used for the same purpose. The
                variance is squared to amplify the effect of large differences. The standard deviation of a
                population is denoted by <MathJax inline>{'$\\sigma$'}</MathJax>, and is calculated as follows.

                <MathJax>{'\\begin{align}\n\\quad \\sigma=\\sqrt{\\frac{\\sum_{i=1}^{N}\\left(x_{i}-\\mu\\right)^{2}}{N}}\n\\end{align}'}</MathJax>

                Meanwhile the sample standard deviation is denoted with <MathJax inline>{'$s$'}</MathJax>, and is calculated with

                <MathJax>{'\\begin{align}\n\\quad S=\\sqrt{\\frac{\\sum_{i=1}^{n}\\left(x_{i}-\\bar{x}\\right)^{2}}{n-1}}\n\\end{align}'}</MathJax>
            </p>

            <h5>Coefficient of Variation</h5>
            <p>
                The coefficient of variation is used for comparison purposes. It is determined by dividing the
                standard deviation by the mean and has no unit of measurement. The coefficient of variation of
                the
                population is

                <MathJax>{'\\begin{align}\nC_v=\\frac{\\sigma}{\\mu}\n\\end{align}'}</MathJax>

                And the coefficient of variation of the sample is

                <MathJax>{'\\begin{align}\n\\hat{C_v}=\\frac{s}{\\bar{x}}\n\\end{align}'}</MathJax>
            </p>

            <h4>Measures of Relationship Between Variables</h4>
            <p>
                Relationship measures are used to discover the relationships between different variables. Higher
                values of the relationship measure are more likely to move the variables together.
            </p>

            <h5>Covariance</h5>
            <p>
                Covariance is the measure of mutual variability. The covariance of a population is calculated
                according to the following formula

                <MathJax>{'\\begin{align}\n\\sigma_{x y}=\\frac{\\sum_{i=1}^{N}\\left(x_{i}-\\mu_{x}\\right) *\\left(y_{i}-\\mu_{y}\\right)}{N}\n\\end{align}'}</MathJax>

                And the sample covariance is calculated with

                <MathJax>{'\\begin{align}\n\\quad S_{x y}=\\frac{\\sum_{i=1}^{n}\\left(x_{i}-\\bar{x}\\right) *\\left(y_{i}-\\bar{y}\\right)}{n-1}\n\\end{align}'}</MathJax>

                <ul>
                    <li>A positive covariance means that the variables grow in the same direction.</li>
                    <li>A negative covariance means that the variables grow in the opposite direction.</li>
                    <li>A covariance of zero means that the variables are independent.</li>
                </ul>

                Note that <MathJax inline>{'$Cov(x,y)=E[xy]-E[x]E[y]$'}</MathJax>. Thus, if <MathJax inline>{'$x=y$'}</MathJax>, <MathJax inline>{'$Cov(x,x)=E[xx]-E[x]E[x]$'}</MathJax> means that
                <MathJax inline>{'$Cov(x,x)=Var(x)$'}</MathJax>.
            </p>

            <h5>Correlation</h5>
            <p>
                Correlation is the normalized version of covariance. Covariance can take any value, while
                correlation can take values between -1 and 1. The population correlation is calculated with

                <MathJax>{'\\begin{align}\n\\rho=\\frac{\\sigma_{x y}}{\\sigma_{x} \\sigma_{y}}\n\\end{align}'}</MathJax>

                And the sample correlation is calculated with

                <MathJax>{'\\begin{align}\n\\mathrm{r}=\\frac{s_{x y}}{s_{x} s_{y}}\n\\end{align}'}</MathJax>

                <ul>
                    <li>If the correlation is equal to 1, there is a perfect correlation. One variable can be fully
                        explained by the other variable.</li>
                    <li>If the correlation is equal to 0, there is no commonality between the variables.</li>
                    <li>If the correlation is equal to -1, there is a perfect negative correlation. This means that
                        the
                        relationship between the variables is always opposite.</li>
                </ul>

                It is crucial to know that correlation does not imply causation.
            </p>

            <h3>Inferential Statistics</h3>
            <p>
                Statistics is about collecting data and drawing inferences from the sample. Inferential
                statistics
                is a branch of statistics that focuses on this purpose. Methods such as parameter estimation and
                hypothesis testing can be used to generalize data to make predictions using the central limit
                theorem, which is proved using the weak law of large numbers.
            </p>

            <h4>Central Limit Theorem</h4>
            <p>
                The weak law of large numbers states that for a sufficiently large sample, the sample mean
                <MathJax inline>{'$M_{n}$'}</MathJax>
                converges to the true mean <MathJax inline>{'$\\mu$'}</MathJax> and can also be expressed as follows: It is unlikely that the
                sample mean <MathJax inline>{'$M_{n}$'}</MathJax> deviates far from the true mean <MathJax inline>{'$\\mu$'}</MathJax>.

                Let <MathJax inline>{'$x_1,x_2,...$'}</MathJax> be independent and identically distributed random variables with finite mean
                <MathJax inline>{'$\\mu$'}</MathJax>
                and variance <MathJax inline>{'$\\sigma^2$'}</MathJax>. Then the sample mean is

                <MathJax>{'\\begin{align}\nM_n=\\frac{x_1+...+x_n}{n}\n\\end{align}'}</MathJax>

                To implement Markov's inequality, the expected value and the variance of <MathJax inline>{'$M_n$'}</MathJax> must first be
                calculated.

                <MathJax>{'\\begin{align}\nE[M_n]&=\\frac{E[x_1+...+x_n]}{n}=\\frac{n\\mu}{n}=\\mu \\\\\nVar(M_n)&=\\frac{Var(x_1+...+x_n)}{n^2}=\\frac{n\\sigma^2}{n^2}=\\frac{\\sigma^2}{n}\n\\end{align}'}</MathJax>

                For a fixed <MathJax inline>{'$\\epsilon>0$'}</MathJax>,

                <MathJax>{'\\begin{align}\nP(|M_n-\\mu|\\geq\\epsilon)\\leq\\frac{Var(M_n)}{n\\epsilon^2}\\xrightarrow[n\\rightarrow\\infty]{}0\n\\end{align}'}</MathJax>

                In conclusion,

                <MathJax>{'\\begin{align}\n\\text{For} \\:\\:\\epsilon>0,\n\\:P(|M_n-\\mu|\\geq\\epsilon)=P(|\\frac{x_1+...+x_n}{n}-\\mu|\\geq\\epsilon)\\rightarrow0\\:\\:\\text{as}\\:\\:n\\rightarrow\\infty\n\\end{align}'}</MathJax>

                The central limit theorem states that for any distribution, the sample distribution of the mean
                converges to the normal distribution.

                Let <MathJax inline>{'$x_1,x_2,...$'}</MathJax> be independent and identically distributed random variables with finite mean
                <MathJax inline>{'$\\mu$'}</MathJax>
                and variance <MathJax inline>{'$\\sigma^2$'}</MathJax>.

                <MathJax>{'\\begin{align}\nZ=\\frac{x_1+x_2+...+x_n-n\\mu}{\\sigma\\sqrt{n}}\n\\end{align}'}</MathJax>

                tends to standard normal as <MathJax inline>{'$n\\rightarrow\\infty$'}</MathJax>.

                <MathJax>{'\\begin{align}\nP\\{ \\frac{x_1+x_2+...+x_n-n\\mu}{\\sigma\\sqrt{n}}\\leq a \\}\\rightarrow \\int _{-\\infty\n            }^0e^{-x^2/2}dx\\:\\:\\text{as}\\:\\:n\\rightarrow\\infty\n\\end{align}'}</MathJax>
            </p>

            <h5>Standard Error</h5>
            <p>
                The standard error is the standard deviation of the sampling distribution. It indicates the
                variability of a sample, as does any standard deviation. It is calculated with the formula

                <MathJax>{'\\begin{align}\n\\sqrt{\\frac{\\sigma^2}{n}}=\\frac{\\sigma}{\\sqrt{n}}\n\\end{align}'}</MathJax>

                where

                <MathJax>{'\\begin{align}\n\\text{Sampling distribution} \\sim N\\left(\\mu,\\frac{\\sigma^2}{n}\\right)\n\\end{align}'}</MathJax>

                The standard error decreases with decreasing sample size, since it is easier to find the true
                mean
                with smaller samples.
            </p>

            <h4>Estimators</h4>
            <p>
                Population parameters such as mean (<MathJax inline>{'$\\mu$'}</MathJax>), variance (<MathJax inline>{'$\\sigma^2$'}</MathJax>), and correlation (<MathJax inline>{'$\\rho$'}</MathJax>) are
                approximated from the sample using functions called estimators. The mean estimator is denoted by
                <MathJax inline>{'$\\bar{x}$'}</MathJax>, while the variance estimator is denoted by <MathJax inline>{'$s^2$'}</MathJax> and the correlation estimator by
                <MathJax inline>{'$r$'}</MathJax>.

                An unbiased estimator with minimum variance is considered an optimal estimator. The bias of an
                estimator is the difference between the parameter and the estimate. Consider a variance estimate
                that results as <MathJax inline>{'$\\sigma^2 + b$'}</MathJax>, <MathJax inline>{'$b$'}</MathJax> here is the bias. Note that an unbiased estimator perfectly
                approximates the population parameter.
            </p>

            <h4>Estimates</h4>
            <p>
                Values obtained from estimators are called estimates. Point estimates output a single value,
                while
                confidence intervals output an interval. It is safer to use confidence intervals than point
                estimates.

                The confidence level is calculated as <MathJax inline>{'$(1-\\alpha)$'}</MathJax>. Conventional values for <MathJax inline>{'$\\alpha$'}</MathJax> are <MathJax inline>{'$0.01$'}</MathJax>,
                <MathJax inline>{'$0.05$'}</MathJax>, and <MathJax inline>{'$0.1$'}</MathJax>. Confidence intervals indicate that the estimated parameter is certain to be
                within the calculated range and are calculated using the following formulas.

                For a single population, if the variance is known, the <MathJax inline>{'$z$'}</MathJax> statistic is used. Hence the variance
                is
                equal to <MathJax inline>{'$\\sigma^2$'}</MathJax>. The confidence interval is calculated using

                <MathJax>{'\\begin{align}\n\\bar{x} \\pm z_{\\alpha / 2} \\frac{\\sigma}{\\sqrt{n}}\n\\end{align}'}</MathJax>

                And if the variance is unknown, the <MathJax inline>{'$t$'}</MathJax> statistic is used. Thus the variance is <MathJax inline>{'$s^2$'}</MathJax>. The
                confidence interval is

                <MathJax>{'\\begin{align}\n\\bar{x} \\pm t_{n-1, \\alpha / 2} \\frac{s}{\\sqrt{n}}\n\\end{align}'}</MathJax>

                For two populations with dependent samples, the <MathJax inline>{'$t$'}</MathJax> statistic is used as well. Here, the
                variance is
                <MathJax inline>{'$s_{\\text {difference }}^{2}$'}</MathJax> and the confidence interval is

                <MathJax>{'\\begin{align}\n\\overline{\\mathrm{d}} \\pm t_{n-1, \\alpha / 2} \\frac{s_{d}}{\\sqrt{n}}\n\\end{align}'}</MathJax>

                The <MathJax inline>{'$z$'}</MathJax> statistic is used for two populations with known variances and independent samples. The
                variances are <MathJax inline>{'$\\sigma_{x}^{2}$'}</MathJax> and <MathJax inline>{'$\\sigma_{y}^{2}$'}</MathJax>, while the confidence interval is

                <MathJax>{'\\begin{align}\n(\\bar{x}-\\bar{y}) \\pm z_{\\alpha / 2}\n\\sqrt{\\frac{\\sigma_{x}^{2}}{n_{x}}+\\frac{\\sigma_{y}^{2}}{n_{y}}}\n\\end{align}'}</MathJax>

                When the variance is unknown but considered equal for two populations with independent samples,
                the
                <MathJax inline>{'$t$'}</MathJax> statistic is used. Therefore, the variance is equal to

                <MathJax>{'\\begin{align}\ns_{p}^{2}=\\frac{\\left(n_{x}-1\\right) s_{x}^{2}+\\left(n_{y}-1\\right) s_{y}^{2}}{n_{x}+n_{y}-2}\n\\end{align}'}</MathJax>

                and the confidence interval is calculated using the formula

                <MathJax>{'\\begin{align}\n(\\bar{x}-\\bar{y}) \\pm t_{n_{x}+n_{y}-2, \\alpha / 2}\n\\sqrt{\\frac{s_{p}^{2}}{n_{x}}+\\frac{s_{p}^{2}}{n_{y}}}\n\\end{align}'}</MathJax>

                When the variance is unknown but considered different for two populations with independent
                samples,
                the <MathJax inline>{'$t$'}</MathJax> statistic is also used. The variances are equal to <MathJax inline>{'$s_{x}^{2}$'}</MathJax> and <MathJax inline>{'$s_{y}^{2}$'}</MathJax>, and the
                confidence interval is

                <MathJax>{'\\begin{align}\n(\\bar{x}-\\bar{y}) \\pm t_{\\mathrm{v}, \\alpha / 2}\n\\sqrt{\\frac{s_{x}^{2}}{n_{x}}+\\frac{s_{y}^{2}}{n_{y}}}\n\\end{align}'}</MathJax>
            </p>

            <h4>Hypothesis Testing</h4>
            <p>
                Hypothesis testing is a statistical method used to determine whether the hypothesis made about
                the
                sample is true. The hypothesis to be
                tested is called the null hypothesis, and the possible conditions of the null hypothesis can
                be seen in Table 3. Note the similarities between the confusion matrix.<br/>

                <center>
                    <table>
                        <tr>
                            <th></th>
                            <th>Truth</th>
                            <th></th>
                        </tr>
                        <tr>
                            <th>Decision</th>
                            <th><MathJax inline>{'$H_0$'}</MathJax> is true</th>
                            <th><MathJax inline>{'$H_0$'}</MathJax> is false</th>
                        </tr>
                        <tr>
                            <th>Accept <MathJax inline>{'$H_0$'}</MathJax></th>
                            <td>Confidence level <MathJax inline>{'$(1 - \\alpha )$'}</MathJax></td>
                            <td>Type II error <MathJax inline>{'$(\\beta)$'}</MathJax></td>
                        </tr>
                        <tr>
                            <th>Reject <MathJax inline>{'$H_0$'}</MathJax></th>
                            <td>Type I error <MathJax inline>{'$(\\alpha)$'}</MathJax></td>
                            <td>Power <MathJax inline>{'$(1 - \\beta )$'}</MathJax></td>
                        </tr>
                    </table>
                    <b>Table 3: </b> The hypothesis testing table.
                </center>
                <br/>

                    Another name for the type I error is significance level (Gustafsen, 2022). The minimum
                    value for the significance level to reject the null hypothesis is called the p-value. The
                    desired values for the p-value are close to 0. The null hypothesis is rejected if the test
                    statistic
                    is greater than the critical value. The formulas for the test statistic for different
                    conditions are given below.<br/>

                    For a population with known variance, the formula for the test statistic is

                    <MathJax>{'\\begin{align}\n                    Z=\\frac{\\bar{x}-\\mu_{0}}{\\sigma / \\sqrt{\\mathrm{n}}}\n                    \\end{align}'}</MathJax>

                    And with unknown variance, the formula becomes

                    <MathJax>{'\\begin{align}\n                    T=\\frac{\\bar{x}-\\mu_{0}}{s / \\sqrt{n}}\n                    \\end{align}'}</MathJax>

                    For two populations with dependent samples, the test statistic is

                    <MathJax>{'\\begin{align}\n                    T=\\frac{\\bar{d}-\\mu_{0}}{s_{d} / \\sqrt{n}}\n                    \\end{align}'}</MathJax>

                    For two populations with independent samples, the formula for the test statistic when the
                    variance
                    is
                    known is

                    <MathJax>{'\\begin{align}\n                    Z=\\frac{(\\bar{x}-\\bar{y})-\\mu_{0}}{\\sqrt{\\frac{\\sigma_{x}^{2}}{n_{x}}+\\frac{\\sigma_{y}^{2}}{n_{y}}}}\n                    \\end{align}'}</MathJax>

                    And for two populations with independent samples, when the variance is unknown but considered
                    equal,
                    the
                    test statistic is

                    <MathJax>{'\\begin{align}\n                    T=\\frac{(\\bar{x}-\\bar{y})-\\mu_{0}}{\\sqrt{\\frac{s_{p}^{2}}{n_{x}}+\\frac{s_{p}^{2}}{n_{y}}}}\n                    \\end{align}'}</MathJax>
            </p>
        </div>
        </MathJaxContext>
    )
}